import React, { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../../../button";
import { PollSlideType } from "../poll";
import { usePollContext } from "../../../../stores/poll";
import { usePollResults, useVoteWithAnalytics } from "../../../poll";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import styles from "../styles.modules.scss";
import { parseCTAs } from "../parse-ctas";
import { handleCTAClick } from "..";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { FormattedMessage } from "react-intl";
import { Timer } from "../campaign-parts/timer";
import { findRelatedMediaByTitle } from "../../../../utils/related-media";
import { bridge } from "../../../../utils/bridge";

type DoorStatus = "none" | "selected" | "voted" | "disabled";
type DoorData = { status: DoorStatus; votedNow: boolean };

export const EchoVaultSlide = ({
    pollSlide,
    trackerSlide,
}: {
    pollSlide: PollSlideType;
    trackerSlide: ProgressSlideType;
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const pollId = pollSlide.id;
    const options = pollSlide.options;
    const {
        state: { voteCache, votingState },
        canVote,
        isClosed,
        actions: { refreshPollInfo, vote, openModal },
    } = usePollContext();
    const { optionsWithResults, showResults } = usePollResults(pollId, options);
    const voteInPoll = (optionId: string[]) => vote(pollId, optionId);
    const voteWithAnalytics = useVoteWithAnalytics(pollId, options, voteInPoll);
    // Avoid showing the voted animation again when re-rendering
    const [votedNow, setVotedNow] = useState<boolean>(false);
    const scrollAnimationOver = useRef<boolean>(false);
    const [scrolledBeyondOriginal, setScrolledBeyondOriginal] =
        useState<boolean>(false);
    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>({
        threshold: 0.001,
    });
    const { tracker, milestones, targeting, relatedMedia } = trackerSlide;
    const { title, relatedMedia: pollRelatedMedia } = pollSlide;

    const relatedMediaUrls = useMemo(
        () =>
            relatedMedia
                ?.map((media) => media.image?.url)
                .filter((image) => image !== undefined) || [],
        [relatedMedia],
    );

    const pollRelatedMediaLabels = useMemo(
        () =>
            pollRelatedMedia
                ?.map((media) => {
                    return { title: media.title, label: media.label };
                })
                .filter((label) => label !== undefined) || [],
        [pollRelatedMedia],
    );

    const lastOpenedDoorString =
        pollRelatedMediaLabels.find(({ title }) => title === "last-opened")
            ?.label ?? undefined;

    const lastOpenedDoor = lastOpenedDoorString
        ? Number(lastOpenedDoorString)
        : undefined;

    const lastOpenedDoorSeen =
        localStorage.getItem(
            `echo-vault-seen-door-open-${pollId}-${lastOpenedDoor}`,
        ) === "true";

    const openedDoors = pollRelatedMediaLabels
        .filter(({ title }) => title?.startsWith("opened-door-"))
        .map(({ title, label }) => {
            return {
                door: Number(title?.replace("opened-door-", "")),
                reward: label,
            };
        });

    const disabled =
        pollRelatedMediaLabels.find(({ title }) => title === "disabled")
            ?.label === "true" || false;

    const openedDoorsNumbers = openedDoors.map(({ door }) => door);

    const loaded = useImageLoader(relatedMediaUrls);

    const setupDoorStatuses = ({
        cache,
        pollId,
    }: {
        cache: typeof voteCache;
        pollId: string;
    }): Record<number, DoorData> => {
        if (cache.votes[pollId] !== undefined && cache.votes[pollId] !== null) {
            const votedId = cache.votes[pollId][0];

            const voted = pollSlide.options.find(
                (option) => option.id === votedId,
            );
            const votedDoor = voted?.title
                ? Number(voted.title.replace("door-", ""))
                : null;

            const disabledBaseObject: DoorData = {
                status: "disabled",
                votedNow: false,
            };

            const disabledBase: Record<number, DoorData> = {
                1: disabledBaseObject,
                2: disabledBaseObject,
                3: disabledBaseObject,
                4: disabledBaseObject,
                5: disabledBaseObject,
                6: disabledBaseObject,
                7: disabledBaseObject,
                8: disabledBaseObject,
                9: disabledBaseObject,
                10: disabledBaseObject,
                11: disabledBaseObject,
                12: disabledBaseObject,
                13: disabledBaseObject,
                14: disabledBaseObject,
                15: disabledBaseObject,
                16: disabledBaseObject,
                17: disabledBaseObject,
                18: disabledBaseObject,
                19: disabledBaseObject,
                20: disabledBaseObject,
            };

            if (!votedDoor) {
                return disabledBase;
            }

            if (votedDoor < 1 || votedDoor > 20) {
                return disabledBase;
            }

            if (!votedDoor && !isClosed(pollId) && !canVote(pollId)) {
                return disabledBase;
            }

            disabledBase[votedDoor] = {
                ...disabledBase[votedDoor],
                status: "voted",
                votedNow: votedNow,
            };

            for (let i = 1; i <= 20; i++) {
                if (openedDoorsNumbers.includes(i)) {
                    disabledBase[i] = {
                        status: "none",
                        votedNow: false,
                    };
                }
            }

            return disabledBase;
        }

        const noneBaseObject: DoorData = {
            status: "none",
            votedNow: false,
        };

        return {
            1: noneBaseObject,
            2: noneBaseObject,
            3: noneBaseObject,
            4: noneBaseObject,
            5: noneBaseObject,
            6: noneBaseObject,
            7: noneBaseObject,
            8: noneBaseObject,
            9: noneBaseObject,
            10: noneBaseObject,
            11: noneBaseObject,
            12: noneBaseObject,
            13: noneBaseObject,
            14: noneBaseObject,
            15: noneBaseObject,
            16: noneBaseObject,
            17: noneBaseObject,
            18: noneBaseObject,
            19: noneBaseObject,
            20: noneBaseObject,
        };
    };

    const [doorStatuses, setDoorStatuses] = useState<Record<number, DoorData>>(
        setupDoorStatuses({
            cache: voteCache,
            pollId,
        }),
    );

    useEffect(() => {
        refreshPollInfo(pollId);

        setDoorStatuses(
            setupDoorStatuses({
                cache: voteCache,
                pollId,
            }),
        );
    }, [voteCache, voteCache.avatarId, pollId]);

    const toggleDoor = (doorNumber: number) => {
        if (openedDoorsNumbers.includes(doorNumber)) {
            const openDoor = openedDoors.find(
                (openedDoor) => openedDoor.door === doorNumber,
            );

            const reward = openDoor?.reward;
            if (reward) {
                if (reward === "removeMortis") {
                    return;
                }

                if (reward === "oniKenjiMangaChroma") {
                    const oniKenjiCode =
                        pollRelatedMediaLabels.find(
                            ({ title }) => title === "oni-kenji-code",
                        )?.label ?? undefined;

                    if (!oniKenjiCode) return;

                    claimOniKenjiCode(oniKenjiCode);
                } else {
                    (async () => {
                        (await bridge()).openShop();
                    })();
                }
            }
        }

        setDoorStatuses((prev) => {
            const newStatuses: Record<number, DoorData> = {};
            for (let i = 1; i <= 20; i++) {
                if (i === doorNumber) {
                    newStatuses[i] = {
                        status:
                            prev[i]?.status === "none" &&
                            !openedDoorsNumbers.includes(i)
                                ? "selected"
                                : "none",
                        votedNow: false,
                    };
                } else {
                    newStatuses[i] = {
                        status: "none",
                        votedNow: false,
                    };
                }
            }
            return newStatuses;
        });
    };

    const handleVote = async (doorNumber: number) => {
        const option = options.find(
            (option) => option.title === `door-${doorNumber}`,
        );

        if (option) {
            try {
                await voteWithAnalytics([option.id]);
                refreshPollInfo(pollId);

                if (votingState === "fail") return;

                setVotedNow(true);

                setDoorStatuses(
                    setupDoorStatuses({
                        cache: voteCache,
                        pollId,
                    }),
                );
            } catch (e) {
                console.error("Error voting for door", e);
            }
        }
    };

    useEffect(() => {
        if (!loaded) return;

        const handleScrollEnd = () => {
            if (!scrollRef.current) return;

            const sectionWidth = scrollRef.current.scrollWidth / 3;
            const scrollLeft = scrollRef.current.scrollLeft;

            const sectionRatio = scrollLeft / sectionWidth;
            const currentSection =
                sectionRatio <= 1 ? 1 : Math.ceil(sectionRatio);

            if (currentSection < 2) {
                const newScrollLeft =
                    sectionWidth * 2 + (scrollLeft - sectionWidth);

                scrollRef.current.scrollLeft = newScrollLeft;
            } else if (currentSection > 2) {
                const newScrollLeft =
                    sectionWidth + (scrollLeft - sectionWidth * 2);

                scrollRef.current.scrollLeft = newScrollLeft;
            }
        };

        const handleScroll = () => {
            if (!scrollRef.current) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clearTimeout((scrollRef.current as any)._scrollEndTimer);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (scrollRef.current as any)._scrollEndTimer = setTimeout(() => {
                handleScrollEnd();
            }, 100);

            const sectionWidth = scrollRef.current.scrollWidth / 3;
            const scrollLeft = scrollRef.current.scrollLeft;

            if (
                scrollAnimationOver.current &&
                scrollLeft > sectionWidth &&
                scrollLeft < sectionWidth * 2
            ) {
                setScrolledBeyondOriginal(true);
            }
        };

        setTimeout(() => {
            if (scrollRef.current) {
                const scrollWidth = scrollRef.current.scrollWidth;
                const sectionWidth = scrollWidth / 3;

                // Get random number between 1 and 20, which is not in the openedDoorNumbers array
                const randomStartingDoor = ((): number => {
                    const availableDoors = Array.from(
                        { length: 20 },
                        (_, i) => i + 1,
                    ).filter((door) => !openedDoorsNumbers.includes(door));
                    const randomIndex = Math.floor(
                        Math.random() * availableDoors.length,
                    );

                    return availableDoors[randomIndex] || 1;
                })();

                const startingPoint =
                    sectionWidth +
                    (sectionWidth / 20) * (randomStartingDoor - 2);

                const scrollLead = 500;

                if (lastOpenedDoor !== undefined && !lastOpenedDoorSeen) {
                    const rewardStartingPoint = getClosestDoorScrollToPosition(
                        sectionWidth +
                            (sectionWidth / 20) * (lastOpenedDoor - 2),
                        scrollWidth,
                    );

                    const animationStartingPoint =
                        rewardStartingPoint < scrollLead
                            ? 0
                            : rewardStartingPoint - scrollLead;

                    scrollToDoor({
                        scrollRef,
                        startingPoint: animationStartingPoint,
                        targetPoint: rewardStartingPoint,
                    });

                    setTimeout(() => {
                        scrollAnimationOver.current = true;
                    }, 1200);

                    localStorage.setItem(
                        `echo-vault-seen-door-open-${pollId}-${lastOpenedDoor}`,
                        "true",
                    );
                } else {
                    const fixedStartingPoint = getClosestDoorScrollToPosition(
                        startingPoint,
                        scrollWidth,
                    );

                    const animationStartingPoint =
                        fixedStartingPoint < scrollLead
                            ? 0
                            : fixedStartingPoint - scrollLead;

                    scrollToDoor({
                        scrollRef,
                        startingPoint: animationStartingPoint,
                        targetPoint: fixedStartingPoint,
                    });

                    setTimeout(() => {
                        scrollAnimationOver.current = true;
                    }, 1200);
                }
            }
        }, 150);

        scrollRef.current?.addEventListener("scroll", handleScroll);

        return () => {
            scrollRef.current?.removeEventListener("scroll", handleScroll);
        };
    }, [loaded]);

    const [decor5Animations] = useState(
        Array.from({ length: 20 }, () => {
            const randomSpeed = (Math.random() * (2.5 - 1.5) + 1.5) * 1.5;

            const p1 = (Math.random() * (0.7 - 0.3) + 0.3).toFixed(2);
            const p2 = (Math.random() * (0.7 - 0.3) + 0.3).toFixed(2);
            const randomCubicBezier = `cubic-bezier(${p1}, 0, ${p2}, 1)`;

            return {
                randomSpeed,
                randomCubicBezier,
            };
        }),
    );

    const bottomElementRef = React.useRef<HTMLDivElement>(null);
    const trackerProgressRef = React.useRef<HTMLDivElement>(null);

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const openNewsArticle = useOpenNewsArticle();
    const sendCommand = useSendCommand();

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const relatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            "door-1": "--door-1",
            "door-1-open": "--door-1-open",
            "door-2": "--door-2",
            "door-2-open": "--door-2-open",
            "door-3": "--door-3",
            "door-3-open": "--door-3-open",
            "door-4": "--door-4",
            "door-4-open": "--door-4-open",
            "door-5": "--door-5",
            "door-5-open": "--door-5-open",
            "door-6": "--door-6",
            "door-6-open": "--door-6-open",
            "door-7": "--door-7",
            "door-7-open": "--door-7-open",
            "door-8": "--door-8",
            "door-8-open": "--door-8-open",
            "door-9": "--door-9",
            "door-9-open": "--door-9-open",
            "door-10": "--door-10",
            "door-10-open": "--door-10-open",
            "door-11": "--door-11",
            "door-11-open": "--door-11-open",
            "door-12": "--door-12",
            "door-12-open": "--door-12-open",
            "door-13": "--door-13",
            "door-13-open": "--door-13-open",
            "door-14": "--door-14",
            "door-14-open": "--door-14-open",
            "door-15": "--door-15",
            "door-15-open": "--door-15-open",
            "door-16": "--door-16",
            "door-16-open": "--door-16-open",
            "door-17": "--door-17",
            "door-17-open": "--door-17-open",
            "door-18": "--door-18",
            "door-18-open": "--door-18-open",
            "door-19": "--door-19",
            "door-19-open": "--door-19-open",
            "door-20": "--door-20",
            "door-20-open": "--door-20-open",
            "decor-1": "--decor-1",
            "decor-2": "--decor-2",
            "decor-3": "--decor-3",
            "decor-4": "--decor-4",
            "decor-5": "--decor-5",
            "decor-5-glow": "--decor-5-glow",
            "bg-1": "--bg-1",
            "bg-2": "--bg-2",
            "bg-3": "--bg-3",
            "bg-4": "--bg-4",
            "bg-5": "--bg-5",
            "window-1": "--window-1",
            "window-2": "--window-2",
            board: "--board",
            wall: "--wall",
            floor: "--floor",
            front: "--front",
            ray: "--ray",
            shadow: "--shadow",
            icon: "--icon",
            check: "--check",
            watch: "--watch",
            hand: "--hand",
            "reward-coin": "--reward-coin",
            "reward-coins200": "--reward-coins200",
            "reward-coins1000": "--reward-coins1000",
            "reward-credits500": "--reward-credits500",
            "reward-gem": "--reward-gem",
            "reward-gems20": "--reward-gems20",
            "reward-hyperchargeStarrDrop": "--reward-hyperchargeStarrDrop",
            "reward-removeMortis": "--reward-removeMortis",
            "reward-lumi": "--reward-lumi",
            "reward-megaboxes2": "--reward-megaboxes2",
            "reward-oniKenjiMangaChroma": "--reward-oniKenjiMangaChroma",
            "reward-powerPoints200": "--reward-powerPoints200",
            "reward-powerPoints500": "--reward-powerPoints500",
            "reward-powerPoints1000": "--reward-powerPoints1000",
            "reward-pumpkinBoxes3": "--reward-pumpkinBoxes3",
            "reward-randomStarrDrop": "--reward-randomStarrDrop",
            "reward-randomStarrDrops10": "--reward-randomStarrDrops10",
            "reward-sprayBoo": "--reward-sprayBoo",
            "reward-sushiRolls10": "--reward-sushiRolls10",
            "reward-ultraTrophyBox": "--reward-ultraTrophyBox",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        return styles;
    }, [relatedMedia]);

    const ctas = parseCTAs(trackerSlide.ctas, handleClick);

    const fillPercentage = tracker.progress;
    const barColor = tracker.color;

    const style = {
        width: `${Math.max(fillPercentage, 1.25)}%`,
    };

    const barStyle = {
        "--bar-color": barColor,
        height: "100%",
        ...relatedMediaStyles,
    } as React.CSSProperties;

    const timerString = pollRelatedMedia?.find(
        (media) => media.title === "timer",
    )?.label;

    const oniKenjiCode =
        pollRelatedMediaLabels.find(({ title }) => title === "oni-kenji-code")
            ?.label ?? undefined;

    return (
        <>
            <div
                className={`slide progress-indicator ${styles.slide} ${
                    isVisible ? "visible" : ""
                } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                    targeting?.campaignId ?? ""
                } without-title ${trackerSlide.lang}`}
                ref={ref}
                style={barStyle}
            >
                <div className={`loader ${loaded ? "loaded" : ""}`}></div>
                <GesturingHand
                    shouldHide={scrolledBeyondOriginal}
                    lower={oniKenjiCode !== undefined}
                />
                {votingState === "fail" && (
                    <div className="error">
                        <FormattedMessage id="globalText.langVoteTryAgainLabel" />
                    </div>
                )}
                <div className="walls swiper-no-swiping" ref={scrollRef}>
                    <div className="wall">
                        <WallSet
                            num={1}
                            key="wall-set-1"
                            scrollRef={scrollRef}
                            doorStatuses={doorStatuses}
                            toggleDoor={toggleDoor}
                            handleVote={handleVote}
                            showResults={showResults}
                            optionsWithResults={optionsWithResults}
                            votingState={votingState}
                            decor5Animations={decor5Animations}
                            openedDoors={openedDoors}
                            disabled={disabled}
                        />
                        <WallSet
                            num={2}
                            key="wall-set-2"
                            scrollRef={scrollRef}
                            doorStatuses={doorStatuses}
                            toggleDoor={toggleDoor}
                            handleVote={handleVote}
                            showResults={showResults}
                            optionsWithResults={optionsWithResults}
                            votingState={votingState}
                            decor5Animations={decor5Animations}
                            openedDoors={openedDoors}
                            disabled={disabled}
                        />
                        <WallSet
                            num={3}
                            key="wall-set-3"
                            scrollRef={scrollRef}
                            doorStatuses={doorStatuses}
                            toggleDoor={toggleDoor}
                            handleVote={handleVote}
                            showResults={showResults}
                            optionsWithResults={optionsWithResults}
                            votingState={votingState}
                            decor5Animations={decor5Animations}
                            openedDoors={openedDoors}
                            disabled={disabled}
                        />
                    </div>
                </div>
                <div className="floor"></div>
                <Front scrollRef={scrollRef} />
                {timerString !== undefined && (
                    <Timer
                        timerString={timerString}
                        lang={String(trackerSlide.lang)}
                    />
                )}
                <div className={`content ${styles.content}`}>
                    {title && (
                        <>
                            <div className={`filter ${styles.filter}`} />
                            <div className="titles">
                                <div
                                    className={`title ${styles.title} `}
                                    data-title={title}
                                >
                                    {title}
                                </div>
                                <div className="subtitle">
                                    <FormattedMessage id="globalText.langEchoVaultInstructionTemporary" />
                                </div>
                            </div>
                        </>
                    )}
                    <div className={`trackers ${styles.trackers}`}>
                        {isVisible && (
                            <>
                                <div className="tracker-dice-wrapper">
                                    <div className={`tracker-wrapper`}>
                                        <div
                                            className={`tracker ${styles.tracker} ${tracker.progress === 100 ? "full" : ""}`}
                                        >
                                            <div
                                                style={style}
                                                className={`tracker-progress ${
                                                    styles["tracker-progress"]
                                                } ${
                                                    fillPercentage > 99
                                                        ? "almost-complete"
                                                        : ""
                                                }`}
                                                ref={trackerProgressRef}
                                            >
                                                <RawValue
                                                    tracker={tracker}
                                                    trackerProgressRef={
                                                        trackerProgressRef
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`icon-container ${styles["icon-container"]}`}
                                            style={style}
                                        >
                                            <div
                                                className={`icon ${styles.icon}`}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <div
                            className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                        >
                            {milestones.map((milestone, index) => {
                                if (!milestone.label) {
                                    return <div key={index} />;
                                }

                                const milestonePosition = Math.max(
                                    milestone.progress,
                                    5, // 5% is the minimum we support without the UI looking totally wack
                                );

                                const hasReachedMilestone = milestone.unlocked;

                                const style = {
                                    left: `${milestonePosition}%`,
                                };

                                return (
                                    <div
                                        key={index}
                                        style={style}
                                        className={`milestone-label ${
                                            styles["milestone-label"]
                                        } ${
                                            hasReachedMilestone
                                                ? styles["milestone-reached"]
                                                : styles["milestone-unreached"]
                                        } ${
                                            hasReachedMilestone
                                                ? "milestone-reached"
                                                : "milestone-unreached"
                                        }`}
                                        title={milestone.label}
                                    >
                                        {milestone.label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div ref={bottomElementRef}></div>
                </div>
                <div className={`wrapper ${styles.wrapper}`}>
                    {ctas.length ? (
                        <div
                            className={`ctas ${
                                ctas.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {ctas.map(({ label, variant, onClick }, index) => {
                                return (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={variant}
                                        size="sm"
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                {oniKenjiCode !== undefined && (
                    <div className="oniKenjiButton">
                        <Button
                            onClick={() => {
                                claimOniKenjiCode(oniKenjiCode);
                            }}
                            variant="green"
                            size="sm"
                        >
                            <span className="buttonTextWrapper">
                                <FormattedMessage id="globalText.langEchoVaultClaimTemporary" />
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting?: Targeting;
    lang: Locale;
    relatedMedia?: RelatedMedia[];
};

function WallSet({
    num,
    scrollRef,
    doorStatuses,
    toggleDoor,
    handleVote,
    showResults,
    optionsWithResults,
    votingState,
    decor5Animations,
    openedDoors,
    disabled,
}: {
    num: number;
    scrollRef: React.RefObject<HTMLDivElement>;
    doorStatuses: Record<number, DoorData>;
    toggleDoor: (doorNumber: number) => void;
    handleVote: (doorNumber: number) => void;
    showResults: boolean;
    optionsWithResults: ReturnType<typeof usePollResults>["optionsWithResults"];
    votingState: ReturnType<typeof usePollContext>["state"]["votingState"];
    decor5Animations: {
        randomSpeed: number;
        randomCubicBezier: string;
    }[];
    openedDoors: { door: number; reward: string | undefined }[];
    disabled: boolean;
}) {
    return (
        <>
            <Window
                key={`window-${num}-1`}
                count={1}
                setNum={num}
                scrollRef={scrollRef}
            />
            <Decor5
                randomSpeed={decor5Animations[0]?.randomSpeed}
                randomCubicBezier={decor5Animations[0]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={1}
                data={doorStatuses[1]}
                onToggle={() => toggleDoor(1)}
                onVote={() => handleVote(1)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-1",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 1)
                        ?.reward
                }
                key={`wall-set-${num}-door-1`}
            />
            <Decor5
                randomSpeed={decor5Animations[1]?.randomSpeed}
                randomCubicBezier={decor5Animations[1]?.randomCubicBezier}
            />
            <Decor4 />
            <WallPart
                doorNumber={2}
                data={doorStatuses[2]}
                onToggle={() => toggleDoor(2)}
                onVote={() => handleVote(2)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-2",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 2)
                        ?.reward
                }
                key={`wall-set-${num}-door-2`}
            />
            <Decor5
                randomSpeed={decor5Animations[2]?.randomSpeed}
                randomCubicBezier={decor5Animations[2]?.randomCubicBezier}
            />
            <Decor1 />
            <WallPart
                doorNumber={3}
                data={doorStatuses[3]}
                onToggle={() => toggleDoor(3)}
                onVote={() => handleVote(3)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-3",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 3)
                        ?.reward
                }
                key={`wall-set-${num}-door-3`}
            />
            <Decor3 />
            <Decor5
                randomSpeed={decor5Animations[3]?.randomSpeed}
                randomCubicBezier={decor5Animations[3]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={4}
                data={doorStatuses[4]}
                onToggle={() => toggleDoor(4)}
                onVote={() => handleVote(4)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-4",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 4)
                        ?.reward
                }
                key={`wall-set-${num}-door-4`}
            />
            <Window
                key={`window-${num}-2`}
                count={2}
                setNum={num}
                scrollRef={scrollRef}
            />
            <Decor5
                randomSpeed={decor5Animations[4]?.randomSpeed}
                randomCubicBezier={decor5Animations[4]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={5}
                data={doorStatuses[5]}
                onToggle={() => toggleDoor(5)}
                onVote={() => handleVote(5)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-5",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 5)
                        ?.reward
                }
                key={`wall-set-${num}-door-5`}
            />
            <Decor2 />
            <Decor5
                randomSpeed={decor5Animations[5]?.randomSpeed}
                randomCubicBezier={decor5Animations[5]?.randomCubicBezier}
            />
            <Decor4 />
            <WallPart
                doorNumber={6}
                data={doorStatuses[6]}
                onToggle={() => toggleDoor(6)}
                onVote={() => handleVote(6)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-6",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 6)
                        ?.reward
                }
                key={`wall-set-${num}-door-6`}
            />
            <Decor3 />
            <Decor5
                randomSpeed={decor5Animations[6]?.randomSpeed}
                randomCubicBezier={decor5Animations[6]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={7}
                data={doorStatuses[7]}
                onToggle={() => toggleDoor(7)}
                onVote={() => handleVote(7)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-7",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 7)
                        ?.reward
                }
                key={`wall-set-${num}-door-7`}
            />
            <Decor5
                randomSpeed={decor5Animations[7]?.randomSpeed}
                randomCubicBezier={decor5Animations[7]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={8}
                data={doorStatuses[8]}
                onToggle={() => toggleDoor(8)}
                onVote={() => handleVote(8)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-8",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 8)
                        ?.reward
                }
                key={`wall-set-${num}-door-8`}
            />
            <Window
                key={`window-${num}-3`}
                count={3}
                setNum={num}
                scrollRef={scrollRef}
            />
            <Decor1 />
            <Decor5
                randomSpeed={decor5Animations[8]?.randomSpeed}
                randomCubicBezier={decor5Animations[8]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={9}
                data={doorStatuses[9]}
                onToggle={() => toggleDoor(9)}
                onVote={() => handleVote(9)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-9",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 9)
                        ?.reward
                }
                key={`wall-set-${num}-door-9`}
            />
            <Decor2 />
            <Decor5
                randomSpeed={decor5Animations[9]?.randomSpeed}
                randomCubicBezier={decor5Animations[9]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={10}
                data={doorStatuses[10]}
                onToggle={() => toggleDoor(10)}
                onVote={() => handleVote(10)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-10",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 10)
                        ?.reward
                }
                key={`wall-set-${num}-door-10`}
            />
            <Decor5
                randomSpeed={decor5Animations[10]?.randomSpeed}
                randomCubicBezier={decor5Animations[10]?.randomCubicBezier}
            />
            <Decor4 />
            <WallPart
                doorNumber={11}
                data={doorStatuses[11]}
                onToggle={() => toggleDoor(11)}
                onVote={() => handleVote(11)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-11",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 11)
                        ?.reward
                }
                key={`wall-set-${num}-door-11`}
            />
            <Decor3 />
            <Decor5
                randomSpeed={decor5Animations[11]?.randomSpeed}
                randomCubicBezier={decor5Animations[11]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={12}
                data={doorStatuses[12]}
                onToggle={() => toggleDoor(12)}
                onVote={() => handleVote(12)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-12",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 12)
                        ?.reward
                }
                key={`wall-set-${num}-door-12`}
            />
            <Window
                key={`window-${num}-4`}
                count={4}
                setNum={num}
                scrollRef={scrollRef}
            />
            <Decor5
                randomSpeed={decor5Animations[12]?.randomSpeed}
                randomCubicBezier={decor5Animations[12]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={13}
                data={doorStatuses[13]}
                onToggle={() => toggleDoor(13)}
                onVote={() => handleVote(13)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-13",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 13)
                        ?.reward
                }
                key={`wall-set-${num}-door-13`}
            />
            <Decor1 />
            <Decor5
                randomSpeed={decor5Animations[13]?.randomSpeed}
                randomCubicBezier={decor5Animations[13]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={14}
                data={doorStatuses[14]}
                onToggle={() => toggleDoor(14)}
                onVote={() => handleVote(14)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-14",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 14)
                        ?.reward
                }
                key={`wall-set-${num}-door-14`}
            />
            <Decor5
                randomSpeed={decor5Animations[14]?.randomSpeed}
                randomCubicBezier={decor5Animations[14]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={15}
                data={doorStatuses[15]}
                onToggle={() => toggleDoor(15)}
                onVote={() => handleVote(15)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-15",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 15)
                        ?.reward
                }
                key={`wall-set-${num}-door-15`}
            />
            <Decor2 />
            <Decor5
                randomSpeed={decor5Animations[15]?.randomSpeed}
                randomCubicBezier={decor5Animations[15]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={16}
                data={doorStatuses[16]}
                onToggle={() => toggleDoor(16)}
                onVote={() => handleVote(16)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-16",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 16)
                        ?.reward
                }
                key={`wall-set-${num}-door-16`}
            />
            <Window
                key={`window-${num}-5`}
                count={5}
                setNum={num}
                scrollRef={scrollRef}
            />
            <Decor4 />
            <Decor5
                randomSpeed={decor5Animations[16]?.randomSpeed}
                randomCubicBezier={decor5Animations[16]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={17}
                data={doorStatuses[17]}
                onToggle={() => toggleDoor(17)}
                onVote={() => handleVote(17)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-17",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 17)
                        ?.reward
                }
                key={`wall-set-${num}-door-17`}
            />
            <Decor1 />
            <Decor5
                randomSpeed={decor5Animations[17]?.randomSpeed}
                randomCubicBezier={decor5Animations[17]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={18}
                data={doorStatuses[18]}
                onToggle={() => toggleDoor(18)}
                onVote={() => handleVote(18)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-18",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 18)
                        ?.reward
                }
                key={`wall-set-${num}-door-18`}
            />
            <Decor3 />
            <Decor5
                randomSpeed={decor5Animations[18]?.randomSpeed}
                randomCubicBezier={decor5Animations[18]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={19}
                data={doorStatuses[19]}
                onToggle={() => toggleDoor(19)}
                onVote={() => handleVote(19)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-19",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 19)
                        ?.reward
                }
                key={`wall-set-${num}-door-19`}
            />
            <Decor2 />
            <Decor5
                randomSpeed={decor5Animations[19]?.randomSpeed}
                randomCubicBezier={decor5Animations[19]?.randomCubicBezier}
            />
            <WallPart
                doorNumber={20}
                data={doorStatuses[20]}
                onToggle={() => toggleDoor(20)}
                onVote={() => handleVote(20)}
                votingState={votingState}
                disabled={disabled}
                result={
                    showResults
                        ? optionsWithResults.find(
                              (option) => option.title === "door-20",
                          )?.result
                        : undefined
                }
                reward={
                    openedDoors.find((openedDoor) => openedDoor.door === 20)
                        ?.reward
                }
                key={`wall-set-${num}-door-20`}
            />
            <Window
                key={`window-${num}-1-2`}
                count={1}
                setNum={num}
                scrollRef={scrollRef}
            />
            <Decor5
                randomSpeed={decor5Animations[0]?.randomSpeed}
                randomCubicBezier={decor5Animations[0]?.randomCubicBezier}
            />
        </>
    );
}

function WallPart({
    doorNumber,
    onToggle,
    onVote,
    data,
    result,
    votingState,
    reward,
    disabled,
}: {
    doorNumber: number;
    onToggle: () => void;
    onVote: () => void;
    data?: DoorData;
    result?: number;
    votingState: ReturnType<typeof usePollContext>["state"]["votingState"];
    reward?: string;
    disabled: boolean;
}) {
    return (
        <div
            className={`wall-part ${disabled ? "disabled" : data?.status} ${reward ? "opened" : ""} ${data?.votedNow ? "voted-now" : ""}`}
        >
            {data?.votedNow === true && (
                <div className="voted-overlay">
                    <div className="voted-icon"></div>
                    <div className="voted-text">
                        <FormattedMessage id="globalText.langVoted" />
                    </div>
                </div>
            )}
            {(data?.status === "voted" || data?.status === "disabled") &&
                result !== undefined && (
                    <div
                        className={`result-overlay ${data?.votedNow ? "voted-now" : ""}`}
                    >
                        <div className="result-text">
                            {(Math.round(result * 1000) / 10).toFixed(1)}%
                        </div>
                        <div className="result-shadows"></div>
                        <div className="result-border"></div>
                    </div>
                )}
            <div className="button-wrapper">
                <Button
                    onClick={() => {
                        onVote();
                    }}
                    variant="green"
                    disabled={votingState === "voting"}
                >
                    <span className="buttonTextWrapper">
                        <FormattedMessage id="globalText.langVoteDefaultButtonLabel" />
                    </span>
                </Button>
            </div>
            {reward !== undefined && (
                <div
                    className={`reward reward-${reward} reward-door-${doorNumber}`}
                >
                    <div className="icon"></div>
                </div>
            )}
            <div
                className={`door door-${doorNumber} ${reward ? "opened" : ""}`}
                onClick={() => {
                    if (data?.status === "none") {
                        onToggle();
                    } else if (data?.status === "selected") {
                        onToggle();
                    }
                }}
            ></div>
            <div className="shadow"></div>
        </div>
    );
}

function Window({
    scrollRef,
    setNum,
    count,
}: {
    scrollRef: React.RefObject<HTMLDivElement>;
    setNum: number;
    count: number;
}) {
    const windowScrollRef = useRef<HTMLDivElement>(null);
    const [scrollPos, setScrollPos] = useState<number>(0);

    useEffect(() => {
        const handleScroll = () => {
            if (!scrollRef.current) return;

            const scrollLeft = scrollRef.current.scrollLeft;
            const scrollWidth = scrollRef.current.scrollWidth;

            // setNum is the part of the section of windows, there are a total of 3
            // count is the number of the window in that section, from 1 to 5

            // Each section is scrollWidth / 3 wide
            // Each window (count) in the section should have its own ratio from 0 to 1
            const sectionStart = (scrollWidth / 3) * (setNum - 1);
            const sectionWidth = scrollWidth / 3;
            const windowWidth = sectionWidth / 5; // 5 windows per section

            const windowStart = sectionStart + windowWidth * (count - 2);
            const windowEnd = windowStart + windowWidth;

            // Clamp scrollLeft to window's range
            const clampedScrollLeft = Math.max(
                Math.min(scrollLeft, windowEnd),
                windowStart,
            );

            // Ratio from 0 to 1 for this window
            const partScrollRatio =
                (clampedScrollLeft - windowStart) / windowWidth;

            setScrollPos(-13.5 * partScrollRatio);
        };

        scrollRef.current?.addEventListener("scroll", handleScroll);

        return () => {
            scrollRef.current?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollRef]);

    return (
        <div className="window-wrapper">
            <div className={`frame ${count % 2 ? "type-1" : "type-2"}`}></div>
            <div className="window" ref={windowScrollRef}>
                <div
                    className={`background window-${count}`}
                    style={{
                        transform: `translateX(${scrollPos}%)`,
                    }}
                ></div>
            </div>
        </div>
    );
}

function Front({ scrollRef }: { scrollRef: React.RefObject<HTMLDivElement> }) {
    const frontScrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!scrollRef.current) return;

            const scrollLeft = scrollRef.current.scrollLeft;
            const scrollWidth = scrollRef.current.scrollWidth;
            const sectionWidth = scrollWidth / 3;

            const sectionRatio = scrollLeft / sectionWidth;
            const currentSection =
                sectionRatio <= 1 ? 1 : Math.ceil(sectionRatio);

            const sectionStart = (scrollWidth / 3) * (currentSection - 1);

            const currentSectionProgress = scrollLeft - sectionStart;

            if (frontScrollRef.current) {
                frontScrollRef.current.scrollLeft = currentSectionProgress * 3;
            }
        };

        scrollRef.current?.addEventListener("scroll", handleScroll);

        return () => {
            scrollRef.current?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollRef]);

    return (
        <div className="front-wrapper" ref={frontScrollRef}>
            <div className="fronts">
                <div className="front"></div>
                <div className="front"></div>
                <div className="front"></div>
            </div>
        </div>
    );
}

function Decor1() {
    return (
        <div className="decor-1-wrapper">
            <div className="decor-1"></div>
        </div>
    );
}

function Decor2() {
    return (
        <div className="decor-2-wrapper">
            <div className="decor-2"></div>
        </div>
    );
}

function Decor3() {
    return (
        <div className="decor-3-wrapper">
            <div className="decor-3"></div>
        </div>
    );
}

function Decor4() {
    return (
        <div className="decor-4-wrapper">
            <div className="decor-4"></div>
        </div>
    );
}

function Decor5({
    randomSpeed,
    randomCubicBezier,
}: {
    randomSpeed?: number;
    randomCubicBezier?: string;
}) {
    return (
        <div className="decor-5-wrapper">
            <div className="decor-5">
                <div
                    className="glow"
                    style={{
                        animationDuration: `${randomSpeed ?? 3}s`,
                        animationTimingFunction:
                            randomCubicBezier || "cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                ></div>
            </div>
        </div>
    );
}

function getClosestDoorScrollToPosition(
    scrollLeft: number,
    scrollWidth: number,
) {
    const breakPoints = scrollWidth / 60;

    let closest = breakPoints;
    for (let i = 1; i < 60; i++) {
        const point = breakPoints * i;
        if (Math.abs(point - scrollLeft) < Math.abs(closest - scrollLeft)) {
            closest = point;
        }
    }

    return closest;
}

function smoothScroll({
    scrollRef,
    startingPoint,
    targetPoint,
    duration,
}: {
    scrollRef: React.RefObject<HTMLDivElement>;
    startingPoint: number;
    targetPoint: number;
    duration: number;
}) {
    const change = targetPoint - startingPoint;
    const startTime = performance.now();

    function animateScroll(currentTime: number) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease in-out cubic
        const ease =
            progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        if (!scrollRef.current) return;

        scrollRef.current.scrollLeft = startingPoint + change * ease;

        if (elapsed < duration) {
            requestAnimationFrame(animateScroll);
        }
    }

    requestAnimationFrame(animateScroll);
}

function useImageLoader(urls: string[]) {
    const [imageLoadCount, setImageLoadCount] = useState(0);
    const [imageUrls, setImageUrls] = useState<string[]>();

    useEffect(() => {
        setImageLoadCount(0);
        setImageUrls(urls);
    }, [urls]);

    useEffect(() => {
        if (!imageUrls) return;
        imageUrls.forEach((url) => {
            const image = new Image();
            image.src = url;
            image.onload = () => {
                setImageLoadCount((count) => count + 1);
            };
            image.onerror = () => {
                setImageLoadCount((count) => count + 1);
            };
        });
    }, [imageUrls]);

    const loaded = imageLoadCount === imageUrls?.length;

    return loaded;
}

function scrollToDoor({
    scrollRef,
    startingPoint,
    targetPoint,
}: {
    scrollRef: React.RefObject<HTMLDivElement>;
    startingPoint: number;
    targetPoint: number;
}) {
    if (!scrollRef.current) return;

    scrollRef.current.scrollLeft = startingPoint;

    smoothScroll({
        scrollRef,
        startingPoint,
        targetPoint,
        duration: 1000,
    });
}

function RawValue({
    tracker,
    trackerProgressRef,
}: {
    tracker: ProgressTracker;
    trackerProgressRef: React.RefObject<HTMLDivElement>;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [fitsLeft, setFitsLeft] = useState<boolean>(false);

    useEffect(() => {
        if (!ref.current || !trackerProgressRef.current) return;

        setFitsLeft(
            ref.current.clientWidth + 20 <
                trackerProgressRef.current.clientWidth,
        );
    }, []);

    if (!tracker.rawProgressInMillions) return null;

    return (
        <div
            className="rawValue"
            ref={ref}
            style={{
                left: !fitsLeft ? "calc(100% + 1rem)" : "0.6rem",
            }}
        >
            <div className="rawValueText">
                {tracker.rawProgressInMillions}
                <FormattedMessage id="globalText.langEchoVaultMillionTemporary" />
            </div>
        </div>
    );
}

function GesturingHand({
    shouldHide,
    lower,
}: {
    shouldHide: boolean;
    lower: boolean;
}) {
    const [visible, setVisible] = useState<boolean>(false);

    useEffect(() => {
        const showTimer = setTimeout(() => {
            if (shouldHide) return;
            setVisible(true);
        }, 2500);

        const hideTimer = setTimeout(() => {
            if (!shouldHide) return;
            setVisible(false);
        }, 300);

        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, [shouldHide]);

    return (
        <div
            className={`handWrapper ${visible ? "visible" : ""} ${lower ? "lower" : ""}`}
        >
            <div className="hand"></div>
        </div>
    );
}

function claimOniKenjiCode(code: string) {
    bridge().then(async (b) => {
        b.claimVoucherQuiet(code);
        b.close();
    });
}
